import { useEffect, useRef, useState } from "react";

import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { disablePush, enablePush, pushState, type PushState } from "../engine/push";
import { useApp } from "../state/store";
import { Button } from "./button";
import { Hint } from "./text";

/**
 * The offer to be told when the summary lands.
 *
 * **Where this renders is the whole design.** A notification permission is spent
 * once -- a declined one cannot be re-requested by the page, only undone by the
 * user in browser settings -- so the prompt has to arrive at the moment its value
 * is self-evident rather than at boot, where it reads as a site being pushy and
 * gets dismissed reflexively.
 *
 * That moment is wrap-up. The person has just stopped recording, finalisation is
 * visibly running, and the app is about to tell them (`pollResult`, after five
 * minutes of polling) that it "will be here when you come back". This is the
 * button that makes that sentence true.
 *
 * Renders nothing at all when the deployment has no VAPID keys, when the browser
 * cannot do push, or when the user has already declined. Silence is correct for
 * all three: there is nothing to offer, and an explanation of why a feature is
 * unavailable is a worse use of the space than the progress list beside it.
 */
export function NotifyOffer() {
  const serverConfig = useApp((s) => s.serverConfig);
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const announced = useRef(false);

  useEffect(() => {
    let live = true;
    void pushState(serverConfig).then((s) => {
      if (live) setState(s);
    });
    return () => {
      live = false;
    };
  }, [serverConfig]);

  // The offer is an overlay in the shared taxonomy (#1085): counted once when it can
  // actually be acted on, never while it is still deciding or absent.
  useEffect(() => {
    if (announced.current) return;
    if (state !== "on" && state !== "offerable" && state !== "granted") return;
    announced.current = true;
    captureAnalytics("dialog_viewed", { dialog: "notify_offer" });
  }, [state]);

  // `denied` is included deliberately. The browser will not show a second
  // prompt, so a button here would do nothing when pressed -- and a dead control
  // is worse than an absent one.
  if (state === null || state === "unsupported" || state === "unconfigured" || state === "denied") {
    return null;
  }

  const on = state === "on";

  return (
    <div className="grid gap-hair">
      <Button
        variant="ghost"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            // Called straight out of the click. `requestPermission` needs a user
            // gesture, and awaiting anything before it can cost the gesture --
            // Safari refuses outright, and Chrome quietly downgrades the prompt,
            // which looks to the user like the button did nothing.
            if (on) {
              captureAnalytics("notifications_toggled", { state: "off" });
              captureAnalytics("dialog_dismissed", { dialog: "notify_offer" });
            } else if (Notification.permission !== "default") {
              // Already answered: this is a preference change, not the prompt's answer.
              captureAnalytics("notifications_toggled", { state: "on" });
            }
            setState(on ? await disablePush() : await enablePush(serverConfig));
          } finally {
            setBusy(false);
          }
        }}
      >
        {on ? t("notify.disable") : t("notify.enable")}
      </Button>
      {on ? <Hint>{t("notify.on_hint")}</Hint> : null}
    </div>
  );
}
